import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StandardInputField from '../../Components/StandardComponents/StandardInputField/StandardInputField';
import { getKnowledgeLibraryItems, knowledgeLibraryErrorMessage } from '../../Api/KnowledgeLibraryApi';
import { getKnowledgeLibraryAccess } from '../../Utils/adminUserTypes';
import {
  DOC_TYPE_LABELS,
  DOC_TYPE_OPTIONS,
  JURISDICTION_OPTIONS,
  LICENCE_MODE_OPTIONS,
  TIER_OPTIONS,
  industryOptions,
  legalStatusLabel,
  localDate,
  shortDate,
  tierLabel,
  toYmd,
} from '../../Components/KnowledgeLibrary/knowledgeLibraryConfig';
import KnowledgeDropZone from './KnowledgeDropZone';
import KnowledgeVersionDialog from './KnowledgeVersionDialog';
import KnowledgeFamilyDialog from './KnowledgeFamilyDialog';
import { POLL_LIMIT, POLL_MS, indexFiles } from './knowledgeDropIn';
import { DROP_DEFAULTS, environmentFromApi, familyOf, overdueLaws } from './knowledgeHelpers';

const FIELD = { uiStyle: 'field_styling_25', fullWidth: true };
const addLabel = (n) => (n ? `Add and index ${n} ${n === 1 ? 'file' : 'files'}` : 'Add and index files');
const GRID = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, columnGap: 2 };

/**
 * Trinity Knowledge: drop files into this environment's knowledge library, the RAG store the
 * Zavvy industry experts search. Each file is uploaded, saved as a Published library item and
 * indexed. The environment is the build's own (one IAM and AI backend per Trinity build); it is
 * the only required field. Laws carry versions and amendments, and a freshness list shows laws
 * whose review date has passed. The System > Knowledge Library page stays the full editor.
 */
const TrinityKnowledge = ({ apiBaseUrl, pollMs = POLL_MS, pollLimit = POLL_LIMIT }) => {
  const access = useMemo(() => getKnowledgeLibraryAccess(), []);
  const environment = useMemo(() => environmentFromApi(apiBaseUrl || process.env.REACT_APP_IAM_SERVICE_BASE_URL), [apiBaseUrl]);
  const [entries, setEntries] = useState([]);
  const [details, setDetails] = useState(DROP_DEFAULTS);
  const [running, setRunning] = useState(false);
  const [items, setItems] = useState([]);
  const [listError, setListError] = useState('');
  const [versionAction, setVersionAction] = useState({ open: false, mode: 'version', row: null });
  const [family, setFamily] = useState({ open: false, familyKey: '', title: '' });
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const res = await getKnowledgeLibraryItems();
      if (!aliveRef.current) return;
      setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
      setListError('');
    } catch (error) {
      if (aliveRef.current) setListError(knowledgeLibraryErrorMessage(error, 'Could not load the library'));
    }
  }, []);

  useEffect(() => { if (access.create) loadItems(); }, [access.create, loadItems]);

  const setDetail = (name, value) => setDetails((d) => ({ ...d, [name]: value }));
  const onDetail = (e) => setDetail(e.target.name, e.target.value);
  const onDate = (e) => setDetail(e.target.name, e.target.value instanceof Date ? toYmd(e.target.value) : (e.target.value || ''));

  const queued = entries.filter((e) => e.state === 'queued');
  const start = async () => {
    if (!queued.length || running || !environment) return;
    const batch = entries.map((e, i) => ({ ...e, index: i })).filter((e) => e.state === 'queued');
    setRunning(true);
    await indexFiles({
      files: batch.map((e) => e.file),
      details,
      onUpdate: (n, patch) => {
        if (!aliveRef.current) return;
        const target = batch[n].index;
        setEntries((list) => list.map((e, i) => (i === target ? { ...e, ...patch } : e)));
      },
      pollMs,
      pollLimit,
      alive: () => aliveRef.current,
    });
    if (!aliveRef.current) return;
    setRunning(false);
    loadItems();
  };

  const laws = items.filter((r) => r.doc_type === 'law');
  const overdue = overdueLaws(items);

  if (!access.create) {
    return <Box sx={{ p: 4 }}><Typography>Only Super Admin and Tech Admin can add to the knowledge library.</Typography></Box>;
  }

  const field = ({ config, ...props }) => (
    <StandardInputField onChange={onDetail} disabled={running} {...props}
      config={{ ...FIELD, ...(props.type === 'select' ? { searchable: true } : {}), ...(config || {}) }} />
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, color: '#0F172A' }}>Knowledge</Typography>
      <Typography sx={{ color: '#64748B', mb: 2 }}>
        Files dropped here are indexed into this environment&apos;s knowledge library, which the Zavvy industry experts search.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ maxWidth: 420 }}>
          <StandardInputField
            type="text"
            name="environment"
            label="Environment"
            required
            disabled
            value={environment ? `${environment.key} (${environment.host})` : 'Not set'}
            helperText="Set by this Trinity build. Files are indexed here only."
            config={FIELD}
          />
        </Box>
        {!environment ? <Alert severity="error" sx={{ mb: 2 }}>This build has no API host, so files cannot be indexed.</Alert> : null}

        <KnowledgeDropZone
          entries={entries}
          disabled={running || !environment}
          onAdd={(files) => setEntries((list) => [...list, ...files.map((file) => ({ file, state: 'queued', message: '' }))])}
          onRemove={(i) => setEntries((list) => list.filter((_, n) => n !== i))}
        />

        <Accordion disableGutters sx={{ mt: 2, boxShadow: 'none', border: '1px solid #E5E7EB', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="knowledge-details" id="knowledge-details-header">
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Details</Typography>
              <Typography sx={{ fontSize: 13, color: '#64748B' }}>
                Optional. Defaults: {tierLabel(details.authority_tier)}, {DOC_TYPE_LABELS[details.doc_type]}, {details.jurisdiction}, {details.licence_mode === 'cite_only' ? 'cite only' : 'quote allowed'}, every expert.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails id="knowledge-details">
            {field({ type: 'text', name: 'title', label: 'Title', value: details.title, helperText: 'Leave blank to use each file name. Used only when one file is added.' })}
            <Box sx={GRID}>
              {field({ type: 'text', name: 'short_name', label: 'Short name', value: details.short_name, helperText: 'How citations name it, such as PDPA' })}
              {field({ type: 'select', name: 'authority_tier', label: 'Tier', value: details.authority_tier, options: TIER_OPTIONS })}
              {field({ type: 'select', name: 'doc_type', label: 'Document type', value: details.doc_type, options: DOC_TYPE_OPTIONS })}
              {field({ type: 'select', name: 'jurisdiction', label: 'Jurisdiction', value: details.jurisdiction, options: JURISDICTION_OPTIONS })}
              {field({ type: 'select', name: 'licence_mode', label: 'Licence mode', value: details.licence_mode, options: LICENCE_MODE_OPTIONS })}
              {field({ type: 'date', name: 'as_of', label: 'As of', value: localDate(details.as_of), onChange: onDate, config: { formatPattern: 'yyyy-MM-dd' } })}
            </Box>
            {field({ type: 'select', name: 'industries', label: 'Industries', value: details.industries, options: industryOptions(), helperText: 'None means every expert can find it', config: { multiple: true, placeholder: 'Every expert' } })}
            {field({ type: 'text', name: 'source_url', label: 'Source URL', value: details.source_url, config: { placeholder: 'https://' } })}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2 }}>
              {field({ type: 'text', name: 'frameworks', label: 'Frameworks', value: details.frameworks, helperText: 'Acronyms, separated by commas' })}
              {field({ type: 'text', name: 'sectors', label: 'Sectors', value: details.sectors, helperText: 'Separated by commas' })}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" onClick={start} disabled={!queued.length || running || !environment} sx={{ textTransform: 'none' }}>
            {running ? 'Adding and indexing...' : addLabel(queued.length)}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Laws due for review</Typography>
        {overdue.length ? (
          <Box component="ul" aria-label="Laws due for review" sx={{ m: 0, pl: 2 }}>
            {overdue.map((r) => (
              <li key={r.id}>
                <Typography component="span" sx={{ fontSize: 14 }}>{r.title}</Typography>
                <Typography component="span" sx={{ fontSize: 13, color: '#B45309', ml: 1 }}>review due {shortDate(r.review_due)}</Typography>
                {r.source_url ? <Typography component="a" href={r.source_url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13, ml: 1 }}>Check the source</Typography> : null}
              </li>
            ))}
          </Box>
        ) : <Typography sx={{ fontSize: 14, color: '#64748B' }}>No law is past its review date.</Typography>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Laws</Typography>
        {listError ? <Alert severity="error" sx={{ mb: 1 }}>{listError}</Alert> : null}
        <TableContainer>
          <Table size="small" aria-label="Laws">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell><TableCell>Version</TableCell><TableCell>Legal status</TableCell>
                <TableCell>Effective from</TableCell><TableCell>Tier</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {laws.map((r) => (
                <TableRow key={r.id} data-testid={`law-${r.id}`}>
                  <TableCell>
                    {r.title}
                    {r.amends_family_key ? <Chip size="small" label={`Amends ${r.amends_family_key}`} sx={{ ml: 1, height: 20 }} /> : null}
                  </TableCell>
                  <TableCell>{r.version_label || '-'}</TableCell>
                  <TableCell>{legalStatusLabel(r.legal_status) || '-'}</TableCell>
                  <TableCell>{shortDate(r.effective_from) || '-'}</TableCell>
                  <TableCell>{tierLabel(r.authority_tier)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setVersionAction({ open: true, mode: 'version', row: r })}>Add new version</Button>
                    <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setVersionAction({ open: true, mode: 'amendment', row: r })}>Add amendment</Button>
                    <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setFamily({ open: true, familyKey: familyOf(r), title: r.title })}>Versions</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!laws.length ? <TableRow><TableCell colSpan={6}>No laws in this library yet.</TableCell></TableRow> : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <KnowledgeVersionDialog
        open={versionAction.open}
        mode={versionAction.mode}
        row={versionAction.row}
        pollMs={pollMs}
        pollLimit={pollLimit}
        onClose={() => setVersionAction((v) => ({ ...v, open: false }))}
        onDone={loadItems}
      />
      <KnowledgeFamilyDialog
        open={family.open}
        familyKey={family.familyKey}
        title={family.title}
        onClose={() => setFamily((f) => ({ ...f, open: false }))}
      />
    </Box>
  );
};

export default TrinityKnowledge;
